// Pure reducer over prd.md §2's state machine. No DOM, no fetch — fully unit-testable, and the
// one place that decides what state comes next, so AdPolicyCheckerElement.ts only has to call
// `reduce()` and re-render, never hand-roll a transition inline.
import type { EmailGateTrigger, Finding, MainState, ScanUrlStatus, UrlProgress, WidgetState } from "./types.js";

export type Action =
  | { type: "VALIDATION_ERROR"; message: string }
  | { type: "VALIDATION_OK" }
  | { type: "SUBMIT" }
  | { type: "SUBMIT_ERROR"; message: string }
  | { type: "RETRY_TO_FORM" }
  | { type: "SCAN_CREATED"; scanId: string; domain: string; urls: UrlProgress[] }
  | { type: "PERMALINK_LOADING" }
  | { type: "PERMALINK_NOT_FOUND" }
  | {
      type: "POLL_UPDATE";
      scanId: string;
      status: "queued" | "running" | "partial" | "complete" | "failed";
      urls: UrlProgress[];
      domain: string;
      domainScore: number | null;
      findings: Finding[];
    }
  | { type: "RESCAN" }
  | { type: "EMAIL_GATE_OPEN"; trigger: EmailGateTrigger }
  | { type: "EMAIL_GATE_CLOSE" }
  | { type: "EMAIL_SUBMIT" }
  | { type: "EMAIL_SUCCESS" }
  | { type: "EMAIL_ERROR"; message: string };

const TERMINAL_URL_STATUSES = new Set<ScanUrlStatus>([
  "done",
  "timeout",
  "bot_blocked",
  "invalid_url",
  "no_ads_found",
  "cached",
]);

function isUrlSuccessful(status: ScanUrlStatus): boolean {
  return status === "done" || status === "cached" || status === "no_ads_found";
}

function reduceMain(main: MainState, action: Action): MainState {
  switch (action.type) {
    case "VALIDATION_ERROR":
      return main.kind === "form" ? { kind: "form", validationError: action.message } : main;
    case "VALIDATION_OK":
      return main.kind === "form" ? { kind: "form", validationError: null } : main;
    case "SUBMIT":
      return { kind: "submitting" };
    case "SUBMIT_ERROR": // F1
      return { kind: "submit_error", message: action.message };
    case "RETRY_TO_FORM":
    case "RESCAN":
      return { kind: "form", validationError: null };
    case "PERMALINK_LOADING":
      // Reuse "submitting" as the loading placeholder — S6's "resolving a permalink" and S2's
      // "waiting for POST /scan to answer" are visually the same "please wait" moment.
      return { kind: "submitting" };
    case "PERMALINK_NOT_FOUND": // S6, unknown/expired job id
      return { kind: "permalink_not_found" };
    case "SCAN_CREATED":
      return { kind: "scanning", scanId: action.scanId, domain: action.domain, urls: action.urls };
    case "POLL_UPDATE": {
      if (action.status === "queued" || action.status === "running") {
        return { kind: "scanning", scanId: action.scanId, domain: action.domain, urls: action.urls };
      }
      if (action.status === "failed") {
        return { kind: "all_failed", scanId: action.scanId, urls: action.urls }; // F4
      }
      // "complete" or "partial" -> S4, even if some individual URLs failed (S3b/c/d) as long
      // as at least one succeeded — prd.md §2.2's F4 is reserved for *every* URL failing.
      const anySuccessful = action.urls.some((u) => isUrlSuccessful(u.status));
      if (!anySuccessful) {
        return { kind: "all_failed", scanId: action.scanId, urls: action.urls };
      }
      return {
        kind: "report",
        scanId: action.scanId,
        domain: action.domain,
        urls: action.urls,
        domainScore: action.domainScore,
        findings: action.findings,
      };
    }
    default:
      return main;
  }
}

function reduceEmailGate(state: WidgetState, action: Action): WidgetState["emailGate"] {
  switch (action.type) {
    case "EMAIL_GATE_OPEN":
      return { status: "open", trigger: action.trigger, errorMessage: null };
    case "EMAIL_GATE_CLOSE":
      return { status: "closed", trigger: null, errorMessage: null };
    case "EMAIL_SUBMIT":
      return { ...state.emailGate, status: "submitting" };
    case "EMAIL_SUCCESS": // S5a
      return { ...state.emailGate, status: "success", errorMessage: null };
    case "EMAIL_ERROR": // S5b
      return { ...state.emailGate, status: "error", errorMessage: action.message };
    case "RESCAN":
    case "SCAN_CREATED":
      return { status: "closed", trigger: null, errorMessage: null };
    default:
      return state.emailGate;
  }
}

export function reduce(state: WidgetState, action: Action): WidgetState {
  return {
    main: reduceMain(state.main, action),
    emailGate: reduceEmailGate(state, action),
  };
}

export { TERMINAL_URL_STATUSES, isUrlSuccessful };
